import type { IReportCampaign } from '~/modules/campaign-analytics/types';
import type { EAnalyticMode } from '~/modules/analytics/types';
import type { IOrderPageAnalytic } from '../types';
import type { IOrder } from '@gem-sdk/analytics';
import type { ReactNode } from 'react';

import { createContext, useContext, useMemo } from 'react';
import { useFetchLatestOrders } from '../hooks';

export interface IOrderListingLatestDataContext {
  totalStoreOrders: number;
  totalFilteredOrders: number;
  orderListingLatest: IOrder[];
  isLoadingLatest: boolean;
}

const OrderListingLatestDataContext = createContext<IOrderListingLatestDataContext | undefined>(
  undefined,
);

interface IOrderListingLatestDataProviderProps {
  mode: EAnalyticMode;
  reportCampaign?: IReportCampaign;
  pageAnalytic?: IOrderPageAnalytic;
  /** Sample orders to show instead of fetching (page analytics demo) */
  demoData?: IOrderListingLatestDataContext;
  children: ReactNode;
}

export const OrderListingLatestDataProvider = ({
  demoData,
  children,
  ...props
}: IOrderListingLatestDataProviderProps) => {
  if (demoData) {
    return (
      <OrderListingLatestDataContext.Provider value={demoData}>
        {children}
      </OrderListingLatestDataContext.Provider>
    );
  }

  return (
    <OrderListingLatestDataProviderReal {...props}>{children}</OrderListingLatestDataProviderReal>
  );
};

const OrderListingLatestDataProviderReal = ({
  mode,
  reportCampaign,
  pageAnalytic,
  children,
}: Omit<IOrderListingLatestDataProviderProps, 'demoData'>) => {
  const { totalStoreOrders, totalFilteredOrders, orderListingLatest, isLoadingLatest } =
    useFetchLatestOrders({
      reportCampaign,
      pageAnalytic,
      mode,
    });

  const contextValue = useMemo(
    () => ({
      totalStoreOrders,
      totalFilteredOrders,
      orderListingLatest,
      isLoadingLatest,
    }),
    [totalStoreOrders, totalFilteredOrders, orderListingLatest, isLoadingLatest],
  );

  return (
    <OrderListingLatestDataContext.Provider value={contextValue}>
      {children}
    </OrderListingLatestDataContext.Provider>
  );
};

export const useOrderListingLatestDataContext = () => {
  const context = useContext(OrderListingLatestDataContext);
  if (!context) {
    throw new Error('GEMX_CONTEXT_IS_NOT_DEFINED');
  }
  return context;
};
